import {
  Component,
  Input,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { environment } from '../../../../../environments/environment';

import { menuLinks } from '@core/constants';
import { ILink, IProfileLink } from '@shared/models/links';
import { FeatureFlagsService } from '@core/services/feature-flags.service';
import { ProfileLinksService } from '@core/services/profile-links.service';
import { IframeModalService } from '@shared/components/iframe-modal/iframe-modal.service';

import { AppState } from '@state/app.state';
import { Store } from '@ngrx/store';
import { selectAuthUser } from '@state/auth/auth.selectors';
import { setAuthModal } from '@state/auth/auth.actions';
import { selectOrganizationUrlSlug } from '@state/organization/organization.selectors';

@UntilDestroy()
@Component({
  selector: 'app-profile-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileSidebarComponent implements OnInit {
  @Input() embed = false;
  @Input() homescreen = false;
  @Input() width: string | undefined | null;
  @Input() showMenuLinks = false;

  protected links: (IProfileLink & {
    isActive: boolean;
    children?: (ILink & { isActive?: boolean })[];
  })[] = [];
  protected menuLinks: ILink[] = menuLinks;

  protected showSubmenu: number[] = [];
  protected currentPath?: string;
  protected isProduction = environment.production;
  protected isLoggedIn = false;

  protected featureFlags: { [key: string]: boolean } = {};

  // External org related
  protected orgUrlSlug: string | null | undefined = null;
  protected isExternalOrg = false;

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private featureFlagsService: FeatureFlagsService,
    private profileLinksService: ProfileLinksService,
    private store: Store<AppState>,
    private iframeModal: IframeModalService,
    private activatedRoute: ActivatedRoute,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.profileLinksService
      .getProfileLinks()
      .pipe(untilDestroyed(this))
      .subscribe((links) => {
        this.links = links.map((item) => ({
          ...item,
          isActive: false,
          children: item.children?.map((child) => ({
            ...child,
            isActive: false,
          })),
        }));
        this.updateActivePaths();
        this.cdr.markForCheck();
      });

    this.store
      .select(selectAuthUser)
      .pipe(untilDestroyed(this))
      .subscribe((user) => {
        this.isLoggedIn = !!user;
        this.cdr.markForCheck();
      });

    if (this.currentPath === undefined && this.router.url !== undefined) {
      this.currentPath = this.router.url;
    }

    // this.router.events.pipe(untilDestroyed(this)).subscribe((event) => {
    //   if (event instanceof NavigationEnd) {
    //     this.currentPath = event.urlAfterRedirects;
    //     this.updateActivePaths(); // <== important!
    //   }
    // });
    this.activatedRoute.url.pipe(untilDestroyed(this)).subscribe(() => {
      this.currentPath = this.router.url;
      this.updateActivePaths();
      this.cdr.markForCheck();
    });

    this.openSubmenuOnInit();

    this.featureFlagsService
      .returnAll()
      .pipe(untilDestroyed(this))
      .subscribe((featureFlags) => {
        this.featureFlags = featureFlags;
        this.cdr.markForCheck();
      });

    this.store
      .select(selectOrganizationUrlSlug)
      .pipe(untilDestroyed(this))
      .subscribe((urlSlug) => {
        this.orgUrlSlug = urlSlug?.toLowerCase();
        this.isExternalOrg =
          !!this.orgUrlSlug &&
          !!this.currentPath?.includes(`o/${this.orgUrlSlug}`);
        this.cdr.markForCheck();
      });
  }

  trackByFn(index: number, item: ILink): string {
    return item.title;
  }

  showToastr(e: Event): void {
    e.preventDefault();
    this.toastr.warning('This feature is not available yet');
  }

  openSubmenuOnInit(): void {
    const path = this.router.url;
    // Strip query parameters for comparison
    const pathWithoutQuery = path.split('?')[0];
    this.links.forEach((link, index) => {
      if (link.children) {
        link.children.forEach((child) => {
          if (
            child.path === pathWithoutQuery ||
            child.pathsToCheck?.some((p) => pathWithoutQuery.includes(p))
          ) {
            this.showSubmenu = [...this.showSubmenu, index];
          }
        });
      }
    });
    this.cdr.markForCheck();
  }

  showAuthModal() {
    this.store.dispatch(setAuthModal({ authModal: 'signin' }));
  }

  updateActivePaths(): void {
    // Strip query parameters from currentPath for comparison
    const currentPathWithoutQuery = this.currentPath?.split('?')[0];

    this.links = this.links.map((item) => {
      const isItemActive = !!(
        item.path === currentPathWithoutQuery ||
        item.pathsToCheck?.some((path) => {
          if (path === '/') {
            return currentPathWithoutQuery === '/';
          }
          return currentPathWithoutQuery?.startsWith(path + '/');
        })
      );

      const updatedChildren = item.children?.map((subItem) => ({
        ...subItem,
        isActive: !!(
          subItem.path === currentPathWithoutQuery ||
          subItem.pathsToCheck?.some(
            (path) => currentPathWithoutQuery?.startsWith(path)
          )
        ),
      }));

      return {
        ...item,
        isActive: isItemActive,
        children: updatedChildren,
      };
    });
    this.cdr.markForCheck();
  }

  showIframeModal(source: string | null): void {
    if (source) {
      this.iframeModal.open(source, undefined, true);
    }
  }

  reloadRoute(path: string | null) {
    const currentUrl = this.router.url;
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigateByUrl(path || currentUrl);
    });
  }
}
